import React, { FC } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// Components
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
  Theme
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
// Types
import { formatDate, formatMoney } from '../../../helpers';
import { ICheckInvoice } from '../../../models';
import { Link } from '../../../components';

interface ICheckInvoiceColumns extends ICheckInvoice {
  date: string;
}

interface ICheckDetailsMobile {
  original: ICheckInvoiceColumns;
}

export const CheckDetailsMobile: FC<ICheckDetailsMobile> = ({ original }) => {
  const classes = useStyles();
  const { invoiceNumber, invoiceAmount, dateSubmitted, date, description, paidAmount, recNum } =
    original;

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography className={classes.activeName} variant="body2">
            {invoiceNumber}
          </Typography>
          <Divider orientation="vertical" flexItem className={classes.divider} />
          <Typography className={classes.activeName} variant="body2">
            {description}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Invoice No.:
          </FormLabel>
          <Link className={classes.link} to={`/vendors/invoices/${recNum}`}>
            <Typography component="span" variant="body2">
              {invoiceNumber}
            </Typography>
          </Link>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Submit Date:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatDate(dateSubmitted)}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Paid Date:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatDate(date)}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Description:
          </FormLabel>
          <Typography component="span" variant="body2">
            {description}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Invoice Amount:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatMoney(invoiceAmount)}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Paid Amount:
          </FormLabel>
          <Typography component="span" variant="body2">
            {formatMoney(paidAmount)}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  link: {
    fontSize: theme.spacing(1)
  },
  activeName: {
    color: theme.palette.grey[500]
  },
  divider: {
    margin: '0 5px'
  }
}));
